import Link from 'next/link'
import { FC, Fragment, useCallback, useMemo } from 'react'
import React from 'react'
import styled from 'styled-components'

import {
  AttributeSection,
  ColorSelector,
  TextLink,
  TypeStyleHeadlineMicro,
} from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../../../app/_providers/LocalizationProvider.client'
import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useFixturesContext } from '../../../../../../brunswick/containers/fixtures/FixturesContext'
import { useUpdateQueryParams } from '../../../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { trackEvent } from '../../../../../../lib/analytics'
import { intlPrettyCurrency } from '../../../../../../lib/utils/currency'
import { ColorPriceRange, getValidatedFit } from '../../../../../../lib/utils/product-availability'
import { ProductColorGroupWithUnavailable } from '../../../../../../types/figs'
import { ProductColor } from '../../../../../../types/graphql/product'
import { TooltipContentTypes } from '../../../../../../types/graphql/site-fixtures'
import { ColorGroupTooltip } from '../../../../../attribute-selectors/ColorAttribute'
import { SoldOut } from '../../../../../attribute-selectors/ColorAttribute/styles'
import { ProductDetailsContainer } from '../../contexts/ProductDetailsContainer'
import { SectionWrapper } from '../styles'

import * as Styles from './styles'

const AttributeSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(3)};
`

type PriceBucketedColors = Array<
  ColorPriceRange & { colors: ProductColorGroupWithUnavailable['colors'] }
>

type ProductColorGroupWithPricing = ProductColorGroupWithUnavailable & {
  priceBucketedColors: PriceBucketedColors
}

const PriceBucketLabel = styled(TypeStyleHeadlineMicro)`
  line-height: ${o => o.theme.typography.font.lineHeight.loose};
  display: flex;
  margin-bottom: 2px;
  margin-bottom: ${o => o.theme.spacing(1)};
  margin-top: ${o => o.theme.spacing(2)};
  column-gap: ${o => o.theme.spacing(2)};

  > * {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: auto;
  }
`

export const Price = styled.span`
  color: ${o => o.theme.color.deprecated.obsidianLight};
`

export const MarkedDownPrice = styled.span`
  color: ${o => o.theme.color.deprecated.red.dark};
`

export const CompareAtPrice = styled.span`
  text-decoration: line-through;
  color: ${o => o.theme.color.deprecated.obsidianLighter};
`

export const ColorSection: FC<{
  colorGroups: ProductColorGroupWithUnavailable[]
  retainColorGroupLabel?: boolean
  allowPriceBasedGrouping?: boolean
}> = ({ colorGroups: groups, retainColorGroupLabel = false, allowPriceBasedGrouping = false }) => {
  const { region, locale } = useLocalization()
  const {
    magnolia: {
      priceInfo: { percentageOff, upToPercentageOff },
    },
    detailsSection: { colorTitle, soldOutSectionTitleMessage },
  } = useTranslation()
  const { tooltips } = useFixturesContext()

  const {
    productGroup,
    featuredProduct,
    selectedColorKey,
    selectedSize,
    availableProducts,
    colorPriceRanges,
    isProductFinalSale,
  } = ProductDetailsContainer.useContainer()

  const colorGroups = useMemo(() => {
    const colorGroups: ProductColorGroupWithPricing[] = []

    groups.forEach(colorGroup => {
      const colorGroupPriceBuckets: PriceBucketedColors = []

      colorGroup.colors.forEach(color => {
        const colorPriceRange = colorPriceRanges[color.rawName]
        if (colorPriceRange) {
          const priceBucket = colorGroupPriceBuckets.find(colorGroupPriceBucket => {
            return (
              colorGroupPriceBucket.priceRange.min === colorPriceRange.priceRange.min &&
              colorGroupPriceBucket.priceRange.max === colorPriceRange.priceRange.max &&
              colorGroupPriceBucket.discountPriceRange?.max ===
                colorPriceRange.discountPriceRange?.max &&
              colorGroupPriceBucket.discountPriceRange?.max ===
                colorPriceRange.discountPriceRange?.max
            )
          })
          if (priceBucket) {
            priceBucket.colors.push(color)
          } else {
            colorGroupPriceBuckets.push({
              ...colorPriceRange,
              colors: [color],
            })
          }
        }
      })

      colorGroupPriceBuckets.sort((thisBucket, thatBucket) => {
        if (thisBucket.discountPriceRange && !thatBucket.discountPriceRange) {
          return -1
        } else if (thisBucket.discountPriceRange && thatBucket.discountPriceRange) {
          return thatBucket.discountPriceRange.min - thisBucket.discountPriceRange.min
        } else {
          return thatBucket.priceRange.min - thisBucket.priceRange.min
        }
      })

      colorGroups.push({
        label: colorGroup.label,
        colors: colorGroup.colors,
        priceBucketedColors: colorGroupPriceBuckets,
      })
    })

    return colorGroups
  }, [colorPriceRanges, groups])

  const { portal } = usePortal()
  const updateQueryParams = useUpdateQueryParams()

  const {
    rawFit: selectedFitKey,
    waitlistable: isProductWaitlistable,
    soldOut: isCurrentColorCompletelySoldOut,
  } = featuredProduct

  const getValidFit = useCallback(
    ({ colorKey, fitKey }: { colorKey: string; fitKey: string | undefined }) =>
      getValidatedFit({
        availableProducts,
        colorKey,
        fitKey,
      }),
    [availableProducts]
  )

  const { genderCategory, rawCategory } = productGroup
  const getColorCollectionUrl = useCallback(
    (color: string | undefined) => {
      const colorSlug = color?.toLowerCase().replace(/\s+/g, '-')
      return `/collections/${colorSlug}-scrubs-${genderCategory}s`
    },
    [genderCategory]
  )
  const getFilterByColorUrl = useCallback(
    (color: string | undefined) => {
      return `/collections/all-scrubs-${genderCategory}s?filters=${encodeURIComponent(
        JSON.stringify({ color: [color] })
      )}`
    },
    [genderCategory]
  )

  const isScrubEssentials =
    (rawCategory === 'Scrub Pants' || rawCategory === 'Scrub Top') && !isProductFinalSale
  const shopAllColorsDecision = useFeatureFlag({
    key: 'shop-all-colors-link-flag',
    defaultVariant: 'shop-all-colors-link-control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isScrubEssentials,
  })
  const showShopAllColors = shopAllColorsDecision === 'shop-all-colors-link-variant'

  /**
   * It is currently possible to have a fit already selected and then click a
   * color selector swatch for which there is no valid product with that
   * selected color/fit combo. A combo may be invalid due to the color/fit not
   * being "available for sale" or not existing at all.
   *
   * Here we check the validity of the fit with the new color selection and if
   * the color/fit combo is invalid, fall back to a valid color/combo fit via
   * the site URL query params.
   */

  const onColorSelect = useCallback(
    (color: ProductColor) => {
      if (!color.rawName) return

      const validatedFit = getValidFit({ colorKey: color.rawName, fitKey: selectedFitKey })
      const newQueryParams: Record<string, string> = {
        color: color.rawName,
      }
      if (validatedFit !== selectedFitKey) {
        newQueryParams['fit'] = validatedFit
      }

      updateQueryParams(newQueryParams)
    },
    [getValidFit, selectedFitKey, updateQueryParams]
  )

  return (
    <SectionWrapper className='multi-colors-section'>
      <AttributeSections data-testid='color-swatch-picker'>
        {colorGroups.map((cg, index) => {
          const tooltip = tooltips?.find(
            t =>
              t.contentType === TooltipContentTypes.ColorCategory && t.enumValue.name === cg.label
          )
          if (cg.colors.length === 0) return null
          const title = colorGroups.length === 1 && !retainColorGroupLabel ? colorTitle : cg.label
          const selectedColor = cg.colors.find(({ rawName }) => rawName === selectedColorKey)
          const isCoreColor = selectedColor?.availability === 'core'
          const selectedColorDisplayName = selectedColor?.name
          const selectedColorRawName = selectedColor?.rawName
          const withTooltip = retainColorGroupLabel || colorGroups.length > 1
          const soldOut = isCurrentColorCompletelySoldOut && !selectedSize && !isProductWaitlistable

          return (
            <AttributeSection
              key={cg.label}
              title={title}
              selection={
                <Styles.DisplayTextWrapper $isCoreColor={isCoreColor}>
                  {selectedColorDisplayName}
                  {Boolean(soldOut && selectedColorDisplayName) && (
                    <SoldOut> {soldOutSectionTitleMessage}</SoldOut>
                  )}
                  {index === 0 && showShopAllColors && (
                    <Styles.ColorLink
                      href={getFilterByColorUrl(selectedColorKey)}
                      url={getColorCollectionUrl(selectedColorRawName)}
                      fallbackUrl={getFilterByColorUrl(selectedColorKey)}
                      {...trackEvent({
                        category: 'product details',
                        action: 'click shop all colors link',
                        label: `Shop ${selectedColorKey}`,
                      })}
                    >
                      Shop {selectedColorKey}
                    </Styles.ColorLink>
                  )}
                </Styles.DisplayTextWrapper>
              }
              tooltip={
                withTooltip &&
                tooltip && (
                  <ColorGroupTooltip title={tooltip.title} description={tooltip?.description} />
                )
              }
              tooltipLabel='Additional color information'
            >
              {allowPriceBasedGrouping ? (
                cg.priceBucketedColors.map((priceBucket, index) => {
                  const percentageOffStatement = priceBucket.discountPriceRange
                    ? priceBucket.discountPriceRange.minPercentageOff !==
                      priceBucket.discountPriceRange.maxPercentageOff
                      ? ` (${upToPercentageOff.replace(
                          '{{percentage}}',
                          priceBucket.discountPriceRange.maxPercentageOff + ''
                        )})`
                      : ` (${percentageOff.replace(
                          '{{percentage}}',
                          priceBucket.discountPriceRange.maxPercentageOff + ''
                        )})`
                    : ''
                  const discountRange = priceBucket.discountPriceRange
                    ? priceBucket.discountPriceRange.min !== priceBucket.discountPriceRange.max
                      ? `${intlPrettyCurrency({
                          cents: priceBucket.discountPriceRange.min,
                          region: region,
                          locale: locale,
                        })}-${intlPrettyCurrency({
                          cents: priceBucket.discountPriceRange.max,
                          region: region,
                          locale: locale,
                        })}${percentageOffStatement}`
                      : `${intlPrettyCurrency({
                          cents: priceBucket.discountPriceRange.min,
                          region: region,
                          locale: locale,
                        })}${percentageOffStatement}`
                    : undefined
                  const priceRange =
                    priceBucket.priceRange.min !== priceBucket.priceRange.max
                      ? `${intlPrettyCurrency({
                          cents: priceBucket.priceRange.min,
                          region: region,
                          locale: locale,
                        })}-${intlPrettyCurrency({
                          cents: priceBucket.priceRange.max,
                          region: region,
                          locale: locale,
                        })}`
                      : `${intlPrettyCurrency({
                          cents: priceBucket.priceRange.min,
                          region: region,
                          locale: locale,
                        })}`
                  return (
                    <Fragment key={index}>
                      {cg.priceBucketedColors.length > 1 ? (
                        <PriceBucketLabel asTag='div'>
                          {discountRange ? (
                            <MarkedDownPrice>{discountRange}</MarkedDownPrice>
                          ) : (
                            <Price>{priceRange}</Price>
                          )}
                        </PriceBucketLabel>
                      ) : null}
                      <ColorSelector
                        onSelectColor={onColorSelect}
                        colors={priceBucket.colors}
                        selectedColorKey={selectedColorRawName}
                        disableMobileHorizontalSwipe
                      />
                    </Fragment>
                  )
                })
              ) : (
                <ColorSelector
                  onSelectColor={onColorSelect}
                  colors={cg.colors}
                  selectedColorKey={selectedColorRawName}
                  disableMobileHorizontalSwipe
                />
              )}
              {portal && cg.label === `${portal.name} Uniform` && (
                <Link href={`/portals/${portal.collectionHandle}`} legacyBehavior passHref>
                  <TextLink
                    as='a'
                    variant={'underline'}
                    href={`/portals/${portal.collectionHandle}`}
                  >
                    {portal.pdpCtaText ?? `Shop ${portal.name} Collection`}
                  </TextLink>
                </Link>
              )}
            </AttributeSection>
          )
        })}
      </AttributeSections>
    </SectionWrapper>
  )
}
